import React, { useState } from 'react';
import StylingMockLayout from '../shared/StylingMockLayout';
import TabNav from 'components/ui/TabNav/TabNav';
import SidebarNav from 'components/ui/SidebarNav/SidebarNav';
import './StylingMockNavigation.css';

/**
 * Styling Mock Navigation Components Page
 * Showcases navigation components (Tabs and Sidebar) with interactive examples
 */
const StylingMockNavigation = () => {
  
  // TabNav state
  const [activeTab, setActiveTab] = useState('resources');
  const [tabCount1, setTabCount1] = useState(12);
  const [tabCount2, setTabCount2] = useState(5);
  const [tabCount3, setTabCount3] = useState(8);
  const [showTabCounts, setShowTabCounts] = useState(true);
  
  // SidebarNav state
  const [activePath, setActivePath] = useState('/dashboard');
  const [sidebarGrouped, setSidebarGrouped] = useState(false);

  // TabNav examples
  const basicTabs = [
    { id: 'resources', label: 'Resources' },
    { id: 'costs', label: 'Cost Pools' },
    { id: 'usage', label: 'Usage' }
  ];

  const tabsWithCounts = [
    { id: 'resources', label: 'Resources', count: tabCount1 },
    { id: 'costs', label: 'Cost Pools', count: tabCount2 },
    { id: 'usage', label: 'Usage', count: tabCount3 }
  ];

  const multipleTabs = [
    { id: 'tenants', label: 'Tenants', count: 15 },
    { id: 'users', label: 'Users', count: 42 },
    { id: 'resources', label: 'Resources', count: 28 },
    { id: 'costs', label: 'Cost Pools', count: 7 },
    { id: 'roles', label: 'Roles', count: 12 }
  ];

  // SidebarNav examples
  const flatSidebarItems = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
    { id: 'resources', label: 'Resources', path: '/?tab=resources' },
    { id: 'costs', label: 'Cost Pools', path: '/costs' },
    { id: 'users', label: 'Users', path: '/users' },
    { id: 'settings', label: 'Settings', path: '/settings' }
  ];

  const groupedSidebarItems = [
    {
      id: 'tenant-resources',
      title: 'Tenant Resources',
      items: [
        { id: 'tenants', label: 'Tenants', path: '/tenants' },
        { id: 'resources', label: 'Resources', path: '/?tab=resources' },
        { id: 'costs', label: 'Cost Pools', path: '/costs' }
      ]
    },
    {
      id: 'user-access',
      title: 'User Access',
      items: [
        { id: 'users', label: 'Users', path: '/users' },
        { id: 'roles', label: 'Roles', path: '/roles' }
      ]
    },
    {
      id: 'support',
      title: 'Support',
      items: [
        { id: 'docs', label: 'Documentation', path: '/docs' },
        { id: 'help', label: 'Help Center', path: '/help' }
      ]
    }
  ];

  return (
    <StylingMockLayout>
      <div className="styling-mock-navigation">
        <div className="styling-mock-section">
          <h2 className="styling-mock-section-title">Navigation Components</h2>
          <p className="styling-mock-section-description">
            Tab navigation and sidebar navigation components that use Button sub-components. 
            Edit the values in real-time to see how components respond. All changes are reflected immediately in the demo.
          </p>
        </div>

        {/* TabNav Section */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">TabNav</h3>
          <p className="styling-mock-section-subtitle">
            Tab navigation component using Button sub-components with optional count badges
          </p>

          {/* Interactive Controls */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Active Tab:</label>
                  <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                    className="styling-mock-select"
                  >
                    <option value="resources">Resources</option>
                    <option value="costs">Cost Pools</option>
                    <option value="usage">Usage</option>
                  </select>
                </div>
                <div className="styling-mock-control-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={showTabCounts}
                      onChange={(e) => setShowTabCounts(e.target.checked)}
                      className="styling-mock-checkbox"
                    />
                    Show Count Badges
                  </label>
                </div>
                {showTabCounts && (
                  <>
                    <div className="styling-mock-control-group">
                      <label>Resources Count:</label>
                      <input
                        type="number"
                        min="0"
                        value={tabCount1}
                        onChange={(e) => setTabCount1(parseInt(e.target.value) || 0)}
                        className="styling-mock-number-input"
                      />
                    </div>
                    <div className="styling-mock-control-group">
                      <label>Cost Pools Count:</label>
                      <input
                        type="number"
                        min="0"
                        value={tabCount2}
                        onChange={(e) => setTabCount2(parseInt(e.target.value) || 0)}
                        className="styling-mock-number-input"
                      />
                    </div>
                    <div className="styling-mock-control-group">
                      <label>Usage Count:</label>
                      <input
                        type="number"
                        min="0"
                        value={tabCount3}
                        onChange={(e) => setTabCount3(parseInt(e.target.value) || 0)}
                        className="styling-mock-number-input"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Basic Tabs */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <h4>Basic Tabs (No Counts)</h4>
                <TabNav
                  tabs={basicTabs}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
                <p className="demo-note">Active tab: {activeTab}</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<TabNav
  tabs={[
    { id: 'resources', label: 'Resources' },
    { id: 'costs', label: 'Cost Pools' },
    { id: 'usage', label: 'Usage' }
  ]}
  activeTab="${activeTab}"
  onTabChange={(tabId) => setActiveTab(tabId)}
/>`}
            </div>
          </div>

          {/* Tabs with Counts */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <h4>Tabs with Count Badges</h4>
                <TabNav
                  tabs={showTabCounts ? tabsWithCounts : basicTabs}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
                <p className="demo-note">Active tab: {activeTab}</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<TabNav
  tabs={[
    { id: 'resources', label: 'Resources', count: ${tabCount1} },
    { id: 'costs', label: 'Cost Pools', count: ${tabCount2} },
    { id: 'usage', label: 'Usage', count: ${tabCount3} }
  ]}
  activeTab="${activeTab}"
  onTabChange={(tabId) => setActiveTab(tabId)}
/>`}
            </div>
          </div>

          {/* Multiple Tabs */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <h4>Multiple Tabs (5+)</h4>
                <TabNav
                  tabs={multipleTabs}
                  activeTab="tenants"
                  onTabChange={(tabId) => console.log('Tab changed:', tabId)}
                />
                <p className="demo-note">Multiple tabs with count badges</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<TabNav
  tabs={[
    { id: 'tenants', label: 'Tenants', count: 15 },
    { id: 'users', label: 'Users', count: 42 },
    { id: 'resources', label: 'Resources', count: 28 },
    { id: 'costs', label: 'Cost Pools', count: 7 },
    { id: 'roles', label: 'Roles', count: 12 }
  ]}
  activeTab="tenants"
  onTabChange={(tabId) => handleTabChange(tabId)}
/>`}
            </div>
          </div>
        </div>

        {/* SidebarNav Section */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">SidebarNav</h3>
          <p className="styling-mock-section-subtitle">
            Sidebar navigation component using Button sub-components with support for flat and grouped navigation
          </p>

          {/* Interactive Controls */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Active Path:</label>
                  <select
                    value={activePath}
                    onChange={(e) => setActivePath(e.target.value)}
                    className="styling-mock-select"
                  >
                    <option value="/dashboard">Dashboard</option>
                    <option value="/?tab=resources">Resources</option>
                    <option value="/costs">Cost Pools</option>
                    <option value="/users">Users</option>
                    <option value="/settings">Settings</option>
                  </select>
                </div>
                <div className="styling-mock-control-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={sidebarGrouped}
                      onChange={(e) => setSidebarGrouped(e.target.checked)}
                      className="styling-mock-checkbox"
                    />
                    Grouped Navigation
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Flat Sidebar */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview sidebar-preview">
                <h4>Flat Sidebar Navigation</h4>
                <SidebarNav
                  items={flatSidebarItems}
                  activePath={activePath}
                  onNavigate={(path) => {
                    setActivePath(path);
                    console.log('Navigate to:', path);
                  }}
                  grouped={false}
                />
                <p className="demo-note">Active path: {activePath}</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<SidebarNav
  items={[
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
    { id: 'resources', label: 'Resources', path: '/?tab=resources' },
    { id: 'costs', label: 'Cost Pools', path: '/costs' },
    { id: 'users', label: 'Users', path: '/users' },
    { id: 'settings', label: 'Settings', path: '/settings' }
  ]}
  activePath="${activePath}"
  onNavigate={(path) => navigate(path)}
  grouped={false}
/>`}
            </div>
          </div>

          {/* Grouped Sidebar */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview sidebar-preview">
                <h4>Grouped Sidebar Navigation</h4>
                <SidebarNav
                  items={groupedSidebarItems}
                  activePath={activePath}
                  onNavigate={(path) => {
                    setActivePath(path);
                    console.log('Navigate to:', path);
                  }}
                  grouped={true}
                />
                <p className="demo-note">Active path: {activePath}</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<SidebarNav
  items={[
    {
      id: 'tenant-resources',
      title: 'Tenant Resources',
      items: [
        { id: 'tenants', label: 'Tenants', path: '/tenants' },
        { id: 'resources', label: 'Resources', path: '/?tab=resources' },
        { id: 'costs', label: 'Cost Pools', path: '/costs' }
      ]
    },
    {
      id: 'user-access',
      title: 'User Access',
      items: [
        { id: 'users', label: 'Users', path: '/users' },
        { id: 'roles', label: 'Roles', path: '/roles' }
      ]
    }
  ]}
  activePath="${activePath}"
  onNavigate={(path) => navigate(path)}
  grouped={true}
/>`}
            </div>
          </div>
        </div>

        {/* Component Composition Section */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Component Composition</h3>
          <p className="styling-mock-section-subtitle">
            Both TabNav and SidebarNav use SecondaryButton components internally, following the outlined button style design system.
          </p>

          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <h4>TabNav Internal Structure</h4>
                <div className="composition-example">
                  <p>TabNav uses <code>SecondaryButton</code> components for each tab:</p>
                  <pre className="composition-code">
{`<TabNav>
  {tabs.map(tab => (
    <SecondaryButton
      variant="outline"
      size="small"
      className="tab-button"
    >
      {tab.label}
      {tab.count && <span className="tab-count">{tab.count}</span>}
    </SecondaryButton>
  ))}
</TabNav>`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <h4>SidebarNav Internal Structure</h4>
                <div className="composition-example">
                  <p>SidebarNav uses <code>SecondaryButton</code> components for navigation items:</p>
                  <pre className="composition-code">
{`<SidebarNav>
  {items.map(item => (
    <SecondaryButton
      variant="outline"
      size="small"
      className="sidebar-nav-item"
    >
      {item.label}
    </SecondaryButton>
  ))}
</SidebarNav>`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StylingMockLayout>
  );
};

export default StylingMockNavigation;










